import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Toast from 'react-native-toast-message';
import { tokenUtils } from '@coffeeplexity/shared';
import { logger } from '../../utils/logger';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: process.env['EXPO_PUBLIC_API_URL'] || 'http://localhost:4000/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add auth token to requests
    const token = await AsyncStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const refreshToken = await AsyncStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(
            `${process.env['EXPO_PUBLIC_API_URL'] || 'http://localhost:4000/api/v1'}/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const { access_token } = response.data;
          await AsyncStorage.setItem('access_token', access_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear all tokens and trigger logout
        logger.log('🔄 Token refresh failed, triggering auto-logout');
        await tokenUtils.clearAllTokens();

        // Show user-friendly message
        Toast.show({
          type: 'info',
          text1: 'Session Expired',
          text2: 'Please log in again',
        });
      }
    }

    // Handle other errors
    const errorMessage =
      error.response?.data?.detail || error.message || 'An error occurred';

    // Show toast for user-facing errors
    if (error.response?.status >= 400 && error.response?.status < 500) {
      Toast.show({
        type: 'error',
        text1: 'Error',
        text2: errorMessage,
      });
    } else if (error.response?.status >= 500) {
      Toast.show({
        type: 'error',
        text1: 'Server Error',
        text2: 'Please try again later.',
      });
    }

    return Promise.reject(error);
  }
);

export { apiClient };
